import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "sonner";
import { resolveStorageUrl } from "@/lib/assets";

interface Client {
  id: string;
  slug: string;
  name: string;
  logo_url: string | null;
  timezone: string;
  currency: string;
  organization_id: string | null;
  onboarding_completed: boolean;
  onboarding_completed_at: string | null;
  latitude: number | null;
  longitude: number | null;
  menu_config: {
    type: "image" | "pdf" | "url" | "native";
    value: string;
    label?: string;
  } | null;
  status: string | null;
  archived_at: string | null;
}

interface ClientSettings {
  id: string;
  client_id: string;
  brand_primary_color: string;
  brand_secondary_color: string;
  brand_background_color: string | null;
  brand_text_color: string | null;
  min_purchase_amount: number;
  min_purchase_currency: string;
  min_purchase_message: string | null;
  game_win_probability: number;
  links: {
    google_review_url?: string | null;
    tripadvisor_url?: string | null;
    instagram_url?: string | null;
    facebook_url?: string | null;
    tiktok_url?: string | null;
  };
  actions_enabled: {
    google_reviews?: boolean;
    tripadvisor?: boolean;
    instagram_follow?: boolean;
    facebook_follow?: boolean;
    tiktok_follow?: boolean;
  };
  from_email: string | null;
  games_enabled?: {
    wheel?: boolean;
    blackjack?: boolean;
    slot?: boolean;
  } | null;
  
  ui_design_direction?: string | null;
  ui_motion_level?: string | null;
  // Newsletter settings
  newsletter_enabled?: boolean | null;
  newsletter_mode?: string | null;
  newsletter_inactive_threshold_days?: number | null;
  newsletter_vip_threshold_coupons?: number | null;
  newsletter_send_day?: number | null;
  newsletter_send_hour?: number | null;
  newsletter_default_validity_days?: number | null;
  boumrank_digest_opt_out?: boolean | null;
}

interface ClientContextType {
  client: Client | null;
  clientSettings: ClientSettings | null;
  loading: boolean;
  refreshClient: () => Promise<void>;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const ClientProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [client, setClient] = useState<Client | null>(null);
  const [clientSettings, setClientSettings] = useState<ClientSettings | null>(null);
  const [loading, setLoading] = useState(true);

  // Extract clientSlug directly from pathname instead of using useParams
  const clientSlug = React.useMemo(() => {
    const match = /^\/c\/([^\/]+)/.exec(location.pathname);
    return match ? match[1] : null;
  }, [location.pathname]);

  const loadClient = async (slug: string) => {
    try {
      setLoading(true);

      // Fetch client
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('id, slug, name, logo_url, timezone, currency, organization_id, onboarding_completed, onboarding_completed_at, latitude, longitude, menu_config, archived_at, status')
        .eq('slug', slug)
        .maybeSingle();

      if (clientError) throw clientError;
      if (!clientData) {
        toast.error(`Client "${slug}" non trouvé`);
        setLoading(false);
        return;
      }

      setClient({ ...clientData, logo_url: resolveStorageUrl(clientData.logo_url) } as any);

      // Fetch client settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('client_settings')
        .select('*')
        .eq('client_id', clientData.id)
        .maybeSingle();

      if (settingsError) throw settingsError;
      
      setClientSettings(settingsData ? ({
        ...settingsData,
        links: (settingsData.links as any) || {},
        actions_enabled: (settingsData.actions_enabled as any) || {}
      } as unknown as ClientSettings) : null);
    } catch (error: any) {
      console.error('Error loading client:', error);
      toast.error('Erreur lors du chargement du client');
    } finally {
      setLoading(false);
    }
  };

  const refreshClient = async () => {
    if (clientSlug) {
      await loadClient(clientSlug);
    }
  };

  useEffect(() => {
    // Check if we're on a client route
    const isClientRoute = location.pathname.startsWith('/c/');
    
    if (isClientRoute && clientSlug) {
      loadClient(clientSlug);
    } else if (!isClientRoute) {
      // If not on a client route, clear client data
      setClient(null);
      setClientSettings(null);
      setLoading(false);
    } else if (isClientRoute && !clientSlug) {
      setLoading(false);
    }
  }, [clientSlug, location.pathname]);

  const value = useMemo(() => ({ client, clientSettings, loading, refreshClient }), [client, clientSettings, loading]);

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = () => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};
